import { useEffect, useState } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { BarChart } from '../charts/BarChart';
import { interpolateColors } from '../charts/colors';
import { formatMoney } from '../../utils/format';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../../context/ToastContext';

const GREEN_RAMP_STOPS = ['#60aa45', '#3f7a29', '#2c591c'];

interface Snapshot {
  snapshot_date: string;
  total: string;
  warr: string | null;
}

interface WhatIfRow {
  asset_class: string;
  label: string;
  amount: string;
  percent: number;
  breach: boolean | null;
}

interface WhatIfResult {
  total: string;
  by_class: WhatIfRow[];
}

function formatPercent(n: string | null): string {
  return n == null ? '—' : `${(Number(n) * 100).toFixed(2)}%`;
}

// Monthly allocation history (see the investments:snapshot-allocation
// scheduled command) plus a stateless what-if scenario calculator — the
// tender's "time-series/trend layer" and "what-if scenario testing" asks.
// Both read from /investment-allocation/{history,what-if}; what-if never
// persists anything, it only recomputes percentages/breaches for display.
export function AllocationTools({ assetClasses }: { assetClasses: { value: string; label: string }[] }) {
  const { settings } = useConfig();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);

  const [snapshots, setSnapshots] = useState<Snapshot[] | null>(null);
  useEffect(() => {
    api
      .get<Snapshot[]>('/investment-allocation/history')
      .then((res) => setSnapshots(res.data))
      .catch(() => setSnapshots([]));
  }, []);

  const [deltas, setDeltas] = useState<Record<string, string>>({});
  const [result, setResult] = useState<WhatIfResult | null>(null);
  const [calculating, setCalculating] = useState(false);

  const calculate = async () => {
    const numericDeltas = Object.fromEntries(
      Object.entries(deltas)
        .filter(([, v]) => v.trim() !== '' && !Number.isNaN(Number(v)))
        .map(([k, v]) => [k, Number(v)]),
    );
    if (Object.keys(numericDeltas).length === 0) {
      setResult(null);
      return;
    }
    setCalculating(true);
    try {
      const res = await api.post<WhatIfResult>('/investment-allocation/what-if', { deltas: numericDeltas });
      setResult(res.data);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed to calculate scenario.');
    } finally {
      setCalculating(false);
    }
  };

  const resetWhatIf = () => {
    setDeltas({});
    setResult(null);
  };

  return (
    <>
      <div className="card">
        <div className="chart-card-header">
          <h2>Allocation Trend</h2>
        </div>
        {snapshots && snapshots.length > 0 ? (
          <>
            <BarChart
              data={snapshots.map((s) => ({ label: s.snapshot_date, values: [Number(s.total)] }))}
              seriesColors={interpolateColors(GREEN_RAMP_STOPS, snapshots.length)}
              formatValue={fmtMoney}
            />
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Snapshot</th>
                    <th className="right">Total</th>
                    <th className="right">WARR</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => (
                    <tr key={s.snapshot_date}>
                      <td>{s.snapshot_date}</td>
                      <td className="right">{fmtMoney(Number(s.total))}</td>
                      <td className="right">{formatPercent(s.warr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="muted">
            {snapshots === null ? 'Loading…' : 'No snapshots recorded yet — the monthly snapshot job runs on the 1st of each month.'}
          </p>
        )}
      </div>

      <div className="card">
        <div className="chart-card-header">
          <h2>What-If Scenario</h2>
        </div>
        <p className="muted">
          Enter a hypothetical VUV change for any asset class and calculate the resulting allocation — nothing is saved.
        </p>
        <div className="form-grid">
          {assetClasses.map((c) => (
            <label key={c.value}>
              {c.label} (VUV change)
              <input
                type="number"
                step="0.01"
                value={deltas[c.value] ?? ''}
                onChange={(e) => setDeltas({ ...deltas, [c.value]: e.target.value })}
                placeholder="0"
              />
            </label>
          ))}
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={resetWhatIf}>
            Reset
          </button>
          <button type="button" className="btn btn-primary" disabled={calculating} onClick={calculate}>
            Calculate
          </button>
        </div>

        {result && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Asset Class</th>
                  <th className="right">Amount</th>
                  <th className="right">% of Portfolio</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {result.by_class.map((row) => (
                  <tr key={row.asset_class}>
                    <td>{row.label}</td>
                    <td className="right">{fmtMoney(Number(row.amount))}</td>
                    <td className="right">{row.percent}%</td>
                    <td>
                      {row.breach === true && <span className="badge badge-urgent">Breach</span>}
                      {row.breach === false && <span className="badge badge-active">OK</span>}
                      {row.breach === null && <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="right">
                    <strong>{fmtMoney(Number(result.total))}</strong>
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
